/**
 * AddIntegrationModal - 3-step wizard for adding new integrations
 * Steps: Discovery → About → Setup
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Search, ChevronDown, ChevronUp, Settings, CheckCircle, RefreshCw, Clock } from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { Progress } from '@/components/application/progress-steps/progress-steps'
import type { ProgressIconType } from '@/components/application/progress-steps/progress-types'
import type { Integration, NewIntegration, IntegrationDetails, Language, MCPServerConfigFrontend, MCPTransportType } from '@/types/integrations'
import { integrationDetails, integrableToolNames } from '@/lib/integrations/integrationDetails'
import { getLocalizedText, getCategoryColor, cleanIntegrationConfig, isValidHttpUrl, isIntegrationAdded } from '@/lib/integrations/integrationUtils'
import { discoverMcpServer } from '@/hooks/api/useIntegrations'
import { useAssistant } from '@/hooks/api/useAssistants'
import { IntegrationSelectionCard } from './IntegrationSelectionCard'
import { OpenTableVerificationCode } from './OpenTableVerificationCode'

interface AddIntegrationModalProps {
  open: boolean
  onClose: () => void
  onSave: (integration: NewIntegration) => Promise<boolean>
  onSaveMcp?: (entry: MCPServerConfigFrontend) => Promise<boolean>
  existingIntegrations: Integration[]
  language: Language
  isValidating?: boolean
  isAdmin?: boolean
  assistantId?: number
}

const STEP_COUNT = 3

export function AddIntegrationModal({
  open,
  onClose,
  onSave,
  onSaveMcp,
  existingIntegrations,
  language,
  isValidating,
  isAdmin,
  assistantId,
}: AddIntegrationModalProps) {
  const { t } = useTranslation()
  
  // State
  const [activeStep, setActiveStep] = useState(0)
  const [selectedIntegrationType, setSelectedIntegrationType] = useState('')
  const [integrationConfig, setIntegrationConfig] = useState<Record<string, any>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)

  // MCP state
  const [mcpDiscoveryUrl, setMcpDiscoveryUrl] = useState('')
  const [mcpDiscoveryLoading, setMcpDiscoveryLoading] = useState(false)
  const [mcpServerInfo, setMcpServerInfo] = useState<any>(null)
  const [mcpConfigSchema, setMcpConfigSchema] = useState<any>(null)
  const [mcpDiscoveryError, setMcpDiscoveryError] = useState<string | null>(null)
  const [mcpAlias, setMcpAlias] = useState('')
  const [mcpUrl, setMcpUrl] = useState('')
  const [mcpType, setMcpType] = useState<MCPTransportType>('streamable_http')
  const [mcpAuthToken, setMcpAuthToken] = useState('')
  const [mcpHeaders, setMcpHeaders] = useState<Array<{ key: string; value: string }>>([])
  const [mcpDynamicConfig, setMcpDynamicConfig] = useState<any>({})

  // Fetch assistant data to check for OpenTable activation
  const { data: assistantData, refetch: refetchAssistant, isRefetching: isRefetchingAssistant } = useAssistant(
    assistantId ?? 0,
    { enabled: !!assistantId }
  )

  // Check if OpenTable is activated by looking at the assistant's tools config
  const assistantTools = (assistantData?.config?.tools ?? []) as Integration[]
  const openTableTool = assistantTools.find(
    (tool: Integration) => tool?.name === 'opentable'
  )
  const openTableRestaurantId = openTableTool?.config?.restaurant_id as number | undefined
  const openTableActivated = !!openTableRestaurantId

  // Initialize config with defaults when integration type changes
  useEffect(() => {
    if (selectedIntegrationType && selectedIntegrationType !== 'mcp_custom') {
      const details = integrationDetails[selectedIntegrationType]
      const initialConfig =
        details?.configFields?.reduce(
          (acc, field) => {
            if (field.defaultValue !== undefined) {
              acc[field.key] = field.defaultValue
            }
            return acc
          },
          {} as Record<string, any>
        ) || {}
      setIntegrationConfig(initialConfig)
    } else {
      setIntegrationConfig({})
    }
  }, [selectedIntegrationType])

  // Reset on close
  const handleClose = () => {
    setActiveStep(0)
    setSelectedIntegrationType('')
    setSearchQuery('')
    setShowAdvancedSettings(false)
    setMcpDiscoveryUrl('')
    setMcpDiscoveryLoading(false)
    setMcpServerInfo(null)
    setMcpConfigSchema(null)
    setMcpDiscoveryError(null)
    setMcpAlias('')
    setMcpUrl('')
    setMcpType('streamable_http')
    setMcpAuthToken('')
    setMcpHeaders([])
    setMcpDynamicConfig({})
    onClose()
  }

  // Refetch assistant data when entering Setup step to check for OpenTable activation
  useEffect(() => {
    if (activeStep === 2 && selectedIntegrationType === 'opentable' && assistantId) {
      refetchAssistant()
    }
  }, [activeStep, selectedIntegrationType, assistantId, refetchAssistant])

  // Pre-populate restaurant_id when OpenTable activation is detected
  useEffect(() => {
    if (selectedIntegrationType === 'opentable' && openTableRestaurantId) {
      setIntegrationConfig(prev => ({
        ...prev,
        restaurant_id: openTableRestaurantId,
      }))
    }
  }, [selectedIntegrationType, openTableRestaurantId])

  // Group integrations by category
  const groupedIntegrations = useMemo(() => {
    return Object.entries(integrationDetails).reduce(
      (acc, [key, details]) => {
        const displayName = getLocalizedText(details.displayName, language)
        const description = getLocalizedText(details.description, language)
        const category = getLocalizedText(details.category, language)

        // Filter by search query
        if (
          searchQuery &&
          !displayName.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !description.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !category.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          return acc
        }

        if (!acc[category]) {
          acc[category] = []
        }
        acc[category].push([key, details])
        return acc
      },
      {} as Record<string, Array<[string, IntegrationDetails]>>
    )
  }, [language, searchQuery])

  // Handlers
  const handleNext = async () => {
    // MCP Discovery step
    if (selectedIntegrationType === 'mcp_custom' && activeStep === 0) {
      setMcpDiscoveryLoading(true)
      try {
        setMcpDiscoveryError(null)
        const result = await discoverMcpServer(mcpDiscoveryUrl)
        setMcpServerInfo(result.server_info || null)
        setMcpUrl(mcpDiscoveryUrl || '')
        setMcpConfigSchema(result.server_info?.config_schema || null)
        if (result.server_info?.alias) {
          setMcpAlias(result.server_info.alias)
        }
        if (!result.discovery_successful) {
          setMcpDiscoveryError(t('resources.assistants.integrations.modal.mcp_discovery_failed'))
          return
        }
        setActiveStep(1)
      } catch (e) {
        setMcpDiscoveryError(t('resources.assistants.integrations.modal.mcp_discovery_error'))
      } finally {
        setMcpDiscoveryLoading(false)
      }
      return
    }
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleSkipDiscovery = () => {
    setMcpUrl(mcpDiscoveryUrl)
    setActiveStep(2)
  }

  const handleSave = async () => {
    // Handle MCP save
    if (selectedIntegrationType === 'mcp_custom') {
      const headersRecord: Record<string, string> = {}
      mcpHeaders.forEach(({ key, value }) => {
        if (key) headersRecord[key] = value
      })

      const entry: MCPServerConfigFrontend = {
        alias: mcpAlias || undefined,
        url: mcpUrl,
        type: mcpType,
        auth_token: mcpAuthToken || undefined,
        headers: Object.keys(headersRecord).length ? headersRecord : undefined,
        config: mcpDynamicConfig || {},
      }

      if (onSaveMcp) {
        const success = await onSaveMcp(entry)
        if (success) handleClose()
      }
      return
    }

    // Handle regular integration save
    let configToSave = { ...integrationConfig }

    // Formitable special handling
    if (selectedIntegrationType === 'formitable') {
      configToSave.auth = integrationConfig.auth || { email: 'pollie-formitable@pollie.ai' }
      if (!configToSave.cross_sell_assistant_id) {
        delete configToSave.cross_sell_time_gap_minutes
      } else if (configToSave.cross_sell_time_gap_minutes === undefined) {
        configToSave.cross_sell_time_gap_minutes = 120
      }
    }

    configToSave = cleanIntegrationConfig(configToSave)

    const newIntegration: NewIntegration = {
      name: selectedIntegrationType,
      config: configToSave,
    }

    const success = await onSave(newIntegration)
    if (success) handleClose()
  }

  // Validation
  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return selectedIntegrationType !== ''
      case 1:
        return selectedIntegrationType !== ''
      case 2: {
        if (selectedIntegrationType === 'mcp_custom') {
          const hasUrl = !!mcpUrl
          const urlValid = hasUrl ? isValidHttpUrl(mcpUrl) : false
          const authRequired = mcpServerInfo?.auth?.required === true
          const authOk = !authRequired || (mcpAuthToken && mcpAuthToken.length > 0)
          return hasUrl && urlValid && authOk
        }
        // OpenTable requires activation before saving
        if (selectedIntegrationType === 'opentable') {
          return openTableActivated
        }
        const details = integrationDetails[selectedIntegrationType]
        if (!details) return false
        return details.configFields.every(
          (field) =>
            !field.required ||
            (integrationConfig[field.key] !== undefined && integrationConfig[field.key] !== '')
        )
      }
      default:
        return false
    }
  }

  // Get visible fields for step 3
  const getVisibleFields = () => {
    const details = integrationDetails[selectedIntegrationType]
    if (!details) return { required: [], optional: [] }

    // Filter out internal auth fields for Formitable
    let fields = details.configFields
    if (selectedIntegrationType === 'formitable') {
      fields = fields.filter((f) => !f.key.startsWith('auth.'))
    }

    // Filter admin-only fields
    if (!isAdmin) {
      fields = fields.filter((f) => !f.adminOnly)
    }

    // Hide fields that are shown conditionally under their parent toggle
    const hasEnableFindBookings = fields.some((f) => f.key === 'enable_find_bookings')
    const hasLateArrivalEnabled = fields.some((f) => f.key === 'late_arrival_enabled')
    fields = fields.filter(
      (f) =>
        f.key !== 'cross_sell_time_gap_minutes' &&
        (!hasEnableFindBookings || f.key !== 'late_arrival_enabled') &&
        (!hasLateArrivalEnabled || f.key !== 'allowed_delay_minutes'),
    )

    return {
      required: fields.filter((f) => f.required),
      optional: fields.filter((f) => !f.required),
    }
  }

  // Render field
  const renderField = (field: any) => {
    const label = getLocalizedText(field.label, language)
    const description = getLocalizedText(field.description, language)
    const details = integrationDetails[selectedIntegrationType]

    if (field.type === 'radio') {
      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          <div className="mt-2 space-y-2">
            {field.options?.map((option: any) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={field.key}
                  value={option.value}
                  checked={(integrationConfig[field.key] ?? field.defaultValue) === option.value}
                  onChange={(e) => {
                    const shouldConvertToNumber = typeof option.value === 'number'
                    const newValue = shouldConvertToNumber ? Number(e.target.value) : e.target.value
                    setIntegrationConfig((prev) => ({ ...prev, [field.key]: newValue }))
                  }}
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {getLocalizedText(option.label, language)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          <select
            value={integrationConfig[field.key] ?? field.defaultValue ?? ''}
            onChange={(e) => {
              const firstOption = field.options?.[0]
              const shouldConvertToNumber = firstOption && typeof firstOption.value === 'number'
              const newValue = shouldConvertToNumber ? Number(e.target.value) : e.target.value
              setIntegrationConfig((prev) => ({ ...prev, [field.key]: newValue }))
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            {field.options?.map((option: any) => (
              <option key={option.value} value={option.value}>
                {getLocalizedText(option.label, language)}
              </option>
            ))}
          </select>
        </div>
      )
    }

    if (field.type === 'boolean') {
      const isFindBookings = field.key === 'enable_find_bookings'
      const isFindBookingsEnabled =
        integrationConfig['enable_find_bookings'] ?? true
      const lateArrivalField = isFindBookings
        ? details?.configFields.find((f) => f.key === 'late_arrival_enabled')
        : null

      const isLateArrival = field.key === 'late_arrival_enabled'
      const isLateArrivalEnabled =
        integrationConfig['late_arrival_enabled'] ?? false
      const delayField = isLateArrival
        ? details?.configFields.find((f) => f.key === 'allowed_delay_minutes')
        : null

      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={integrationConfig[field.key] ?? field.defaultValue ?? false}
              onChange={(e) =>
                setIntegrationConfig((prev) => ({ ...prev, [field.key]: e.target.checked }))
              }
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
          </label>
          {isFindBookings && isFindBookingsEnabled && lateArrivalField && (
            <div className="mt-4">{renderField(lateArrivalField)}</div>
          )}
          {isLateArrival && isLateArrivalEnabled && delayField && (
            <div className="mt-4">{renderField(delayField)}</div>
          )}
        </div>
      )
    }

    // Text/password/number input
    const isCrossSellAssistant = field.key === 'cross_sell_assistant_id'
    const hasAssistantId =
      integrationConfig['cross_sell_assistant_id'] !== '' &&
      integrationConfig['cross_sell_assistant_id'] !== undefined
    const timeGapField = isCrossSellAssistant
      ? details?.configFields.find((f) => f.key === 'cross_sell_time_gap_minutes')
      : null

    return (
      <div key={field.key} className="space-y-2">
        <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</label>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        <input
          type={field.type}
          value={integrationConfig[field.key] ?? field.defaultValue ?? ''}
          onChange={(e) => {
            const rawValue = e.target.value
            const newValue = field.type === 'number' ? (rawValue === '' ? '' : Number(rawValue)) : rawValue
            setIntegrationConfig((prev) => ({ ...prev, [field.key]: newValue }))
          }}
          required={field.required}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
        {isCrossSellAssistant && hasAssistantId && timeGapField && (
          <div className="mt-3 border-l-2 border-gray-200 pl-4 dark:border-gray-700">
            {renderField(timeGapField)}
          </div>
        )}
      </div>
    )
  }

  if (!open) return null

  const selectedDetails = integrationDetails[selectedIntegrationType]
  const { required: requiredFields, optional: optionalFields } = getVisibleFields()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.assistants.integrations.modal.add_title')}
          </h2>
          <button
            onClick={handleClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper - using official Untitled UI Progress Steps */}
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <Progress.IconsWithText
            type="number"
            orientation="horizontal"
            items={[
              t('resources.assistants.integrations.modal.discovery'),
              t('resources.assistants.integrations.modal.about'),
              t('resources.assistants.integrations.modal.setup'),
            ].map((title, index): ProgressIconType => ({
              title,
              status: index < activeStep ? 'complete' : index === activeStep ? 'current' : 'incomplete',
            }))}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* Step 1: Discovery */}
          {activeStep === 0 && (
            <>
              {selectedIntegrationType === 'mcp_custom' ? (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {t('resources.assistants.integrations.modal.mcp_discovery_title')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('resources.assistants.integrations.modal.mcp_discovery_description')}
                  </p>
                  <Input
                    placeholder="https://example.com/mcp"
                    value={mcpDiscoveryUrl}
                    onChange={setMcpDiscoveryUrl}
                    className="max-w-lg"
                  />
                  {mcpDiscoveryError && (
                    <p className="text-sm text-red-500">{mcpDiscoveryError}</p>
                  )}
                </div>
              ) : (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        placeholder={t('resources.assistants.integrations.modal.search_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                    </div>
                  </div>

                  {/* Integration grid by category */}
                  {Object.entries(groupedIntegrations).map(([category, integrations]) => (
                    <div key={category} className="mb-6">
                      <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">
                        {category}
                      </h3>
                      <div className="grid grid-cols-3 gap-3">
                        {integrations.map(([key, details]) => {
                          const isAdded = isIntegrationAdded(key, existingIntegrations, language)
                          const isCurrentlyLive = existingIntegrations.some(
                            (i) => i && i.name === key
                          )
                          return (
                            <IntegrationSelectionCard
                              key={key}
                              integrationKey={key}
                              details={details}
                              isSelected={selectedIntegrationType === key}
                              onClick={() => !isAdded && setSelectedIntegrationType(key)}
                              disabled={isAdded}
                              isLive={isCurrentlyLive}
                              language={language}
                            />
                          )
                        })}
                      </div>
                    </div>
                  ))}

                  {Object.keys(groupedIntegrations).length === 0 && (
                    <div className="py-8 text-center">
                      <p className="text-gray-500 dark:text-gray-400">{t('resources.assistants.integrations.modal.no_integrations_found')}</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Step 2: About */}
          {activeStep === 1 && selectedDetails && (
            <div className="space-y-4">
              {selectedIntegrationType === 'mcp_custom' ? (
                <div className="rounded-lg border border-gray-200 p-6 dark:border-gray-700">
                  <div className="mb-4 flex items-center gap-4">
                    <img
                      src={mcpServerInfo?.icon || '/images/mcp.svg'}
                      alt="MCP"
                      className="h-14 w-14 rounded-lg"
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {mcpServerInfo?.name || t('resources.assistants.integrations.mcp_server')}
                      </h3>
                      {mcpServerInfo?.author && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t('resources.assistants.integrations.mcp.by_author', { author: mcpServerInfo.author })}
                        </p>
                      )}
                    </div>
                  </div>
                  {mcpServerInfo?.description && (
                    <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                      {mcpServerInfo.description}
                    </p>
                  )}
                  {mcpServerInfo?.tags && mcpServerInfo.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {mcpServerInfo.tags.map((tag: string, i: number) => (
                        <span
                          key={`${tag}-${i}`}
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-gray-200 p-6 dark:border-gray-700">
                    <div className="mb-4 flex items-center gap-4">
                      <img
                        src={selectedDetails.iconPath}
                        alt={getLocalizedText(selectedDetails.displayName, language)}
                        className="h-14 w-14"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {getLocalizedText(selectedDetails.displayName, language)}
                        </h3>
                        <span
                          className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                          style={{
                            backgroundColor: getCategoryColor(
                              getLocalizedText(selectedDetails.category, language)
                            ),
                          }}
                        >
                          {getLocalizedText(selectedDetails.category, language)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {getLocalizedText(selectedDetails.description, language)}
                    </p>
                  </div>

                  {/* OpenTable verification code */}
                  {selectedIntegrationType === 'opentable' && assistantId && (
                    <OpenTableVerificationCode assistantId={assistantId} />
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Setup */}
          {activeStep === 2 && (
            <div className="space-y-6">
              {selectedIntegrationType === 'mcp_custom' ? (
                <>
                  <div>
                    <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">
                      {t('resources.assistants.integrations.required_settings')}
                    </h3>
                    <div className="max-w-lg space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('resources.assistants.integrations.mcp.alias')}
                        </label>
                        <Input
                          value={mcpAlias}
                          onChange={setMcpAlias}
                          placeholder={t('resources.assistants.integrations.mcp.alias_placeholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('resources.assistants.integrations.mcp.url_required')}
                        </label>
                        <Input
                          value={mcpUrl}
                          onChange={setMcpUrl}
                          placeholder="https://example.com/mcp"
                          className={mcpUrl && !isValidHttpUrl(mcpUrl) ? 'border-red-500' : ''}
                        />
                        {mcpUrl && !isValidHttpUrl(mcpUrl) && (
                          <p className="text-xs text-red-500">{t('resources.assistants.integrations.mcp.url_invalid')}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('resources.assistants.integrations.mcp.type')}
                        </label>
                        <select
                          value={mcpType}
                          onChange={(e) => setMcpType(e.target.value as MCPTransportType)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        >
                          <option value="streamable_http">streamable_http</option>
                          <option value="sse">sse</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('resources.assistants.integrations.mcp.auth_token')} {mcpServerInfo?.auth?.required && '*'}
                        </label>
                        <Input
                          type="password"
                          value={mcpAuthToken}
                          onChange={setMcpAuthToken}
                          placeholder={t('resources.assistants.integrations.mcp.auth_token_placeholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('resources.assistants.integrations.mcp.headers')}
                        </label>
                        {mcpHeaders.map((row, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              placeholder={t('resources.assistants.integrations.mcp.header_key')}
                              value={row.key}
                              onChange={(value) =>
                                setMcpHeaders((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, key: value } : r))
                                )
                              }
                              className="flex-1"
                            />
                            <Input
                              placeholder={t('resources.assistants.integrations.mcp.header_value')}
                              value={row.value}
                              onChange={(value) =>
                                setMcpHeaders((prev) =>
                                  prev.map((r, i) =>
                                    i === idx ? { ...r, value: value } : r
                                  )
                                )
                              }
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              color="tertiary"
                              size="sm"
                              onPress={() =>
                                setMcpHeaders((prev) => prev.filter((_, i) => i !== idx))
                              }
                              className="text-red-500"
                              iconLeading={X}
                            />
                          </div>
                        ))}
                        <Button
                          type="button"
                          color="secondary"
                          size="sm"
                          onClick={() => setMcpHeaders((prev) => [...prev, { key: '', value: '' }])}
                        >
                          {t('resources.assistants.integrations.mcp.add_header')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* OpenTable status indicator */}
                  {selectedIntegrationType === 'opentable' && (
                    <div className="mb-6">
                      {isRefetchingAssistant ? (
                        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {t('resources.assistants.integrations.opentable.checking_status')}
                          </span>
                        </div>
                      ) : openTableActivated ? (
                        <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                            <div>
                              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                                {t('resources.assistants.integrations.opentable.activated')}
                              </span>
                              <span className="ml-2 text-sm text-green-700 dark:text-green-300">
                                {t('resources.assistants.integrations.opentable.restaurant_id', { id: openTableRestaurantId })}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => refetchAssistant()}
                            className="rounded-lg p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/40"
                            title={t('resources.assistants.integrations.opentable.refresh_status')}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                          <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            <div>
                              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                {t('resources.assistants.integrations.opentable.waiting_activation')}
                              </span>
                              <p className="text-xs text-amber-700 dark:text-amber-300">
                                {t('resources.assistants.integrations.opentable.waiting_activation_description')}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => refetchAssistant()}
                            className="rounded-lg p-2 text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                            title={t('resources.assistants.integrations.opentable.check_status')}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Required fields */}
                  {requiredFields.length > 0 && (
                    <div>
                      <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">
                        {t('resources.assistants.integrations.required_settings')}
                      </h3>
                      <div className="max-w-lg space-y-4">
                        {requiredFields.map(renderField)}
                      </div>
                    </div>
                  )}

                  {/* Optional fields */}
                  {optionalFields.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                        className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-primary"
                      >
                        <Settings className="h-4 w-4" />
                        {t('resources.assistants.integrations.advanced_settings')}
                        <span className="rounded-full border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-600">
                          {t('resources.assistants.integrations.optional_count', { count: optionalFields.length })}
                        </span>
                        {showAdvancedSettings ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>

                      {showAdvancedSettings && (
                        <div className="mt-4 max-w-lg space-y-4 border-l-2 border-gray-200 pl-4 dark:border-gray-700">
                          <p className="text-xs italic text-gray-500 dark:text-gray-400">
                            {t('resources.assistants.integrations.optional_hint')}
                          </p>
                          {optionalFields.map(renderField)}
                        </div>
                      )}
                    </div>
                  )}

                  {requiredFields.length === 0 && optionalFields.length === 0 && (
                    <div className="py-8 text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        {t('resources.assistants.integrations.modal.no_config_required')}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            {activeStep > 0 && (
              <Button type="button" color="secondary" onPress={handleBack}>
                {t('resources.assistants.integrations.modal.back')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {selectedIntegrationType === 'mcp_custom' && activeStep === 0 && (
              <Button type="button" color="secondary" onPress={handleSkipDiscovery}>
                {t('resources.assistants.integrations.modal.skip_discovery')}
              </Button>
            )}
            {activeStep < STEP_COUNT - 1 ? (
              <Button
                type="button"
                onPress={handleNext}
                isDisabled={!isStepValid() || mcpDiscoveryLoading}
                isLoading={mcpDiscoveryLoading}
              >
                {selectedIntegrationType === 'mcp_custom' && activeStep === 0
                  ? t('resources.assistants.integrations.modal.discover')
                  : t('resources.assistants.integrations.modal.next')}
              </Button>
            ) : (
              <Button
                type="button"
                onPress={handleSave}
                isDisabled={!isStepValid() || isValidating}
                isLoading={isValidating}
              >
                {t('resources.assistants.integrations.modal.done')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
